import {PageHeader} from '../ui/PageHeader';
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { Empty, ErrorState, Loading } from "../ui/State";
type Application = {
  case_id?: string;
  id?: string;
  title: string;
  stage: string;
  version: number;
  proposal: string;
  conditions: string;
  decision_reason?: string;
  reason?: string;
};
export function ConsentsAlpha({ portal = false }: { portal?: boolean }) {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [items, setItems] = useState<Application[]>(),
    [selected, setSelected] = useState(q.get("case") ?? ""),
    [data, setData] = useState<{
      application: Application;
      documents: { id: string; versionId: string; title: string }[];
      history: {
        id: string;
        created_at: string;
        detail: { stage: string; reason: string };
      }[];
    }>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: object } | null>(null);
  const load = async () => {
    if (!portal)
      setItems(
        (
          await api<{ items: Application[] }>(
            "/api/alpha/consents?building=" + building,
          )
        ).items,
      );
    if (selected) {
      if (portal)
        setData({
          application: await api<Application>(
            "/api/portal/leaseholder/consents/" + selected,
          ),
          documents: [],
          history: [],
        });
      else setData(await api("/api/alpha/consents/" + selected));
    }
  };
  useEffect(() => {
    setData(undefined);
    void load().catch(setError);
  }, [selected, building, portal]);
  async function write(url: string, body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    try {
      await alphaSave(pending, pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function review(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = portal
      ? {
          proposal: f.get("proposal"),
          expectedVersion: data!.application.version,
        }
      : {
          stage: f.get("stage"),
          conditions: f.get("conditions"),
          reason: f.get("reason"),
          expectedVersion: data!.application.version,
          evidence: f.getAll("evidence").map((id) => {
            const d = data!.documents.find((d) => d.id === id)!;
            return { documentId: d.id, versionId: d.versionId };
          }),
        };
    void write(
      portal
        ? "/api/portal/leaseholder/consents/" + selected + "/respond"
        : "/api/alpha/consents/" + selected + "/review",
      body,
    );
  }
  return (
    <>
      <PageHeader title="Alterations applications"/>
      {portal&&!selected&&<Empty>Open an alterations application from your home to review its recorded proposal and response.</Empty>}
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void write("", {})}>Retry the same save</button>
      )}
      {!portal &&
        (!items ? (
          <Loading />
        ) : (
          <section className="panel">
            <h2>Applications to review</h2>
            {items.length ? (
              items.map((i) => (
                <p key={i.case_id}>
                  <button
                    disabled={busy || !!pending.current}
                    onClick={() => setSelected(i.case_id!)}
                  >
                    {i.title}
                  </button>{" "}
                  · {i.stage}
                </p>
              ))
            ) : (
              <p>No applications for this building.</p>
            )}
          </section>
        ))}
      {selected && !data ? (
        <Loading />
      ) : (
        data && (
          <section className="panel form">
            <h2>{data.application.title}</h2>
            <p>{data.application.stage}</p>
            <p>{data.application.proposal}</p>
            <h3>Conditions</h3>
            <p>
              {data.application.conditions || "No conditions recorded yet."}
            </p>
            <h3>Decision or information request</h3>
            <p>{data.application.reason ?? data.application.decision_reason}</p>
            {(!portal ||
              data.application.stage === "Information requested") && (
              <form key={data.application.version} onSubmit={review}>
                <fieldset
                  disabled={
                    busy ||
                    !!pending.current ||
                    ["Completed", "Refused"].includes(data.application.stage)
                  }
                >
                  <legend>
                    {portal
                      ? "Supply requested information"
                      : "Review application"}
                  </legend>
                  {portal ? (
                    <label>
                      Updated proposal and information
                      <textarea
                        name="proposal"
                        defaultValue={data.application.proposal}
                        required
                        minLength={20}
                        maxLength={5000}
                      />
                    </label>
                  ) : (
                    <>
                      <p>
                        Select supporting documents retained in the building
                        repository. Decision reasons and conditions are visible
                        to the applicant.
                      </p>
                      <label>
                        Next stage
                        <select name="stage">
                          {(data.application.stage === "Submitted"
                            ? ["Under review", "Information requested"]
                            : data.application.stage === "Information requested"
                              ? ["Under review"]
                              : data.application.stage === "Approved"
                                ? ["Completed"]
                                : [
                                    "Information requested",
                                    "Approved",
                                    "Refused",
                                  ]
                          ).map((s) => (
                            <option key={s}>{s}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Reason for applicant
                        <textarea
                          name="reason"
                          required
                          minLength={10}
                          maxLength={5000}
                        />
                      </label>
                      <label>
                        Conditions
                        <textarea
                          name="conditions"
                          defaultValue={data.application.conditions}
                          maxLength={5000}
                        />
                      </label>
                      <fieldset>
                        <legend>
                          Verified supporting or completion evidence
                        </legend>
                        {data.documents.map((d) => (
                          <label key={d.id}>
                            <input
                              type="checkbox"
                              name="evidence"
                              value={d.id}
                            />
                            {d.title}
                          </label>
                        ))}
                      </fieldset>
                    </>
                  )}
                  <button>
                    {portal ? "Submit information" : "Save review"}
                  </button>
                </fieldset>
              </form>
            )}
            {!portal && (
              <>
                <h3>Retained review history</h3>
                {data.history.map((h) => (
                  <p key={h.id}>
                    {h.created_at} · {h.detail.stage} · {h.detail.reason}
                  </p>
                ))}
              </>
            )}
          </section>
        )
      )}
    </>
  );
}
